import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { listLookup } from '../../../api/masterData';
import { addReferredFriend, deleteReferredFriend, listReferredFriends } from '../../../api/leads';
import { WizardValues } from './wizardTypes';

interface Step6Props {
  values: WizardValues;
  setField: <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => void;
  leadId: number | null;
}

export const Step6Marketing = ({ values, setField, leadId }: Step6Props) => {
  const queryClient = useQueryClient();
  const [friendName, setFriendName] = useState('');
  const [friendPhone, setFriendPhone] = useState('');

  const sourcesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-sources'],
    queryFn: () => listLookup('/master-data/lead-sources', { activeOnly: true }),
  });
  const campaignsQuery = useQuery({
    queryKey: ['lookup', '/master-data/campaigns'],
    queryFn: () => listLookup('/master-data/campaigns', { activeOnly: true }),
  });
  const referralTypesQuery = useQuery({
    queryKey: ['lookup', '/master-data/referral-types'],
    queryFn: () => listLookup('/master-data/referral-types', { activeOnly: true }),
  });

  const friendsQuery = useQuery({
    queryKey: ['lead-referred-friends', leadId],
    queryFn: () => listReferredFriends(leadId!),
    enabled: !!leadId,
  });

  const addFriendMutation = useMutation({
    mutationFn: (payload: { name: string; phone?: string }) => addReferredFriend(leadId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-referred-friends', leadId] });
      setFriendName('');
      setFriendPhone('');
    },
  });

  const deleteFriendMutation = useMutation({
    mutationFn: (friendId: number) => deleteReferredFriend(friendId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lead-referred-friends', leadId] }),
  });

  return (
    <Stack spacing={2}>
      <TextField
        select
        label="How did you hear about us?"
        fullWidth
        value={values.leadSourceId}
        onChange={(e) => setField('leadSourceId', e.target.value ? Number(e.target.value) : '')}
      >
        {sourcesQuery.data?.map((s) => (
          <MenuItem key={s.id as number} value={s.id as number}>
            {String(s.name)}
          </MenuItem>
        ))}
      </TextField>

      {/* Ad tracking: the specific advertisement/campaign identifier, kept
          deliberately separate from Lead Source (the channel above) — a
          manual free-text entry, never a master-data dropdown. */}
      <TextField
        label="Ad ID / Name"
        fullWidth
        helperText="Which specific advertisement generated this lead (optional)"
        value={values.adIdName}
        onChange={(e) => setField('adIdName', e.target.value)}
      />

      <TextField
        select
        label="Campaign"
        fullWidth
        value={values.campaignId}
        onChange={(e) => setField('campaignId', e.target.value ? Number(e.target.value) : '')}
      >
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        {campaignsQuery.data?.map((c) => (
          <MenuItem key={c.id as number} value={c.id as number}>
            {String(c.name)}
          </MenuItem>
        ))}
      </TextField>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="Referral type"
          fullWidth
          value={values.referralTypeId}
          onChange={(e) => setField('referralTypeId', e.target.value ? Number(e.target.value) : '')}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {referralTypesQuery.data?.map((r) => (
            <MenuItem key={r.id as number} value={r.id as number}>
              {String(r.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Referral student / referrer name"
          fullWidth
          value={values.referrerName}
          onChange={(e) => setField('referrerName', e.target.value)}
        />
      </Stack>
      <TextField
        label="Referrer contact"
        fullWidth
        value={values.referrerContact}
        onChange={(e) => setField('referrerContact', e.target.value)}
      />

      <Divider />
      <Typography variant="subtitle2">Recommend friends</Typography>
      {!leadId ? (
        <Typography variant="body2" color="text.secondary">
          Complete Step 1 first — friends are saved directly against the lead.
        </Typography>
      ) : (
        <>
          <List dense>
            {friendsQuery.data?.map((f) => (
              <ListItem
                key={f.id}
                divider
                secondaryAction={
                  <IconButton size="small" onClick={() => deleteFriendMutation.mutate(f.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText primary={f.name} secondary={f.phone ?? undefined} />
              </ListItem>
            ))}
          </List>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="Friend's name"
              size="small"
              fullWidth
              value={friendName}
              onChange={(e) => setFriendName(e.target.value)}
            />
            <TextField
              label="Phone"
              size="small"
              fullWidth
              value={friendPhone}
              onChange={(e) => setFriendPhone(e.target.value)}
            />
            <Button
              startIcon={<AddIcon />}
              disabled={!friendName.trim() || addFriendMutation.isPending}
              onClick={() => addFriendMutation.mutate({ name: friendName.trim(), phone: friendPhone.trim() || undefined })}
            >
              Add
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};
